import { useCallback, useEffect, useRef } from 'react';

import { pushViewItemList } from '~/gtm/events/ecommerce';
import { mapProductToGA4Item } from '~/gtm/mappers/productMapper';
import type { GA4Item, ListContext } from '~/gtm/types';
import type { Product } from '~/types/Product';

interface UseViewItemListOptions {
  listContext: ListContext;
  currency: string;
  products: Product[];
  /** Threshold for IntersectionObserver (0-1) */
  threshold?: number;
  /** Debounce time in ms before pushing the event */
  debounceMs?: number;
}

/**
 * Hook to track view_item_list events using IntersectionObserver
 * Returns a ref callback to attach to product cards
 */
export function useViewItemList({
  listContext,
  currency,
  products,
  threshold = 0.5,
  debounceMs = 500,
}: UseViewItemListOptions) {
  // Track which products have been viewed in this pageview
  const viewedProductsRef = useRef<Set<string>>(new Set());
  // Pending items to push
  const pendingItemsRef = useRef<GA4Item[]>([]);
  // Debounce timer
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Map of observed elements - queued before observer is ready
  const observedElementsRef = useRef<Map<Element, { product: Product; index: number }>>(new Map());
  // IntersectionObserver instance
  const observerRef = useRef<IntersectionObserver | null>(null);
  // Store latest listContext and currency for use in callbacks
  const listContextRef = useRef(listContext);
  const currencyRef = useRef(currency);

  // Keep refs in sync
  useEffect(() => {
    listContextRef.current = listContext;
    currencyRef.current = currency;
  }, [listContext, currency]);

  // Reset viewed products when products change (new page/filter)
  useEffect(() => {
    viewedProductsRef.current.clear();
    pendingItemsRef.current = [];
  }, [products, listContext.item_list_id]);

  // Flush pending items
  const flushPendingItems = useCallback(() => {
    if (pendingItemsRef.current.length === 0) return;

    pushViewItemList(pendingItemsRef.current, listContextRef.current, currencyRef.current);
    pendingItemsRef.current = [];
  }, []);

  // Schedule flush with debounce
  const scheduleFlush = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(flushPendingItems, debounceMs);
  }, [flushPendingItems, debounceMs]);

  // Create observer once on mount
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const data = observedElementsRef.current.get(entry.target);
          if (!data) return;

          const { product, index } = data;
          const productId = product.code || String(product.id);

          // Skip if already viewed
          if (viewedProductsRef.current.has(productId)) return;

          // Mark as viewed
          viewedProductsRef.current.add(productId);

          // Map to GA4 item and add to pending
          const ga4Item = mapProductToGA4Item(product, null, listContextRef.current, index);
          pendingItemsRef.current.push(ga4Item);

          // Schedule flush
          scheduleFlush();
        });
      },
      { threshold }
    );

    observerRef.current = observer;

    // Observe any elements that were queued before observer was ready
    observedElementsRef.current.forEach((data, element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
      observerRef.current = null;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [threshold, scheduleFlush]);

  // Ref callback to attach to product cards
  const observeProduct = useCallback(
    (element: HTMLElement | null, product: Product, index: number): (() => void) | undefined => {
      if (!element) return undefined;

      // Store product data for this element
      observedElementsRef.current.set(element, { product, index });

      // Start observing if observer is ready
      if (observerRef.current) {
        observerRef.current.observe(element);
      }

      // Return cleanup function
      return () => {
        observerRef.current?.unobserve(element);
        observedElementsRef.current.delete(element);
      };
    },
    []
  );

  return { observeProduct };
}
